/**
 * `useMessageStream` — renderer-side accumulator for the
 * `vm:message-stream` IPC push channel (cloud-VM supportability
 * TDD §B-1).
 *
 * Subscribes to `vm:message-stream` on mount, accumulates each
 * incoming `VmMessage` into an in-memory buffer, and returns the
 * buffer sorted by `seq`. Sort key invariant from the TDD: `seq` is
 * a monotonic main-process counter (NOT `ts`, because wall-clock
 * can step backwards on NTP correction). The hook never sorts by
 * `ts` even though it's available — only `seq`.
 *
 * # Buffering
 *
 * The buffer is a plain array. Insert is amortised O(log n) by
 * tracking the last-seen `seq` and appending when the incoming
 * message's `seq` is greater than the tail (the common case — the
 * main-process counter never decreases and the IPC delivers in
 * order). When an out-of-order message lands (rare — only on a
 * retry / replay), we fall back to a binary-search insert.
 *
 * # B-1 vs B-2 scope
 *
 * B-1 ships full-in-memory: every message lives in the buffer for
 * the workspace lifetime. B-2 layers a rolling buffer + NDJSON
 * persistence. The hook's return shape is stable so the B-2
 * upgrade is a drop-in.
 *
 * # VM-flag-OFF safety
 *
 * The hook subscribes to the IPC channel unconditionally — the
 * channel is silent when the flag is OFF (the producer never
 * constructs the capture). Even so, the parent `<Workspace.tsx>`
 * gates `<VmDebugPane>` mount on `maintainerMode`, so the hook
 * never even runs in the OFF case.
 */

import { useEffect, useState } from 'react';
import type { VmMessage } from '@volley/crucible-ipc-types/vm-message-stream';

/** Binary search for the insertion index that keeps the buffer sorted
 *  by `seq`. Returns `arr.length` when the message should append. */
function findInsertIndex(arr: VmMessage[], seq: number): number {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    // `(lo+hi)>>1` is the canonical safe-mid for 32-bit ints; our
    // buffers are bounded by user-session length so overflow is
    // structurally impossible, but the shift is no slower than `+/2`.
    const mid = (lo + hi) >>> 1;
    if (arr[mid].seq < seq) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

/**
 * Insert `msg` into `arr` so the array stays sorted by `seq`. Returns
 * the new array reference (functional update — React reconciliation
 * needs a fresh array reference to re-render). Dedupes on `seq`: if
 * a message with the same seq already exists, the new value REPLACES
 * the old (last-write-wins). In practice the producer never emits
 * duplicate seq values; the dedupe is belt-and-braces against an IPC
 * retransmit edge.
 */
export function insertSorted(arr: VmMessage[], msg: VmMessage): VmMessage[] {
  // Fast path: tail append. The main-process counter is monotonic and
  // the IPC delivers in order, so the new seq is almost always the
  // largest seen.
  if (arr.length === 0 || msg.seq > arr[arr.length - 1].seq) {
    return [...arr, msg];
  }
  // Duplicate-seq dedupe — find the existing index and replace.
  const idx = findInsertIndex(arr, msg.seq);
  if (idx < arr.length && arr[idx].seq === msg.seq) {
    const next = arr.slice();
    next[idx] = msg;
    return next;
  }
  // Out-of-order insert — return a new array with the message slotted
  // in. Linear in N but the path is rare (only on retransmits).
  return [...arr.slice(0, idx), msg, ...arr.slice(idx)];
}

/**
 * Accumulated message stream. The renderer's `<MessageStream>`
 * component consumes this array directly — it MUST NOT re-sort or
 * mutate. The hook's invariant is: returned array is non-null,
 * sorted by `seq` ascending, deduped on `seq`.
 */
export interface MessageStreamState {
  messages: VmMessage[];
  /** True after at least one message has been received this session.
   *  Drives the empty-state UI ("No messages yet"). */
  hasReceivedAny: boolean;
  /** Imperative clear — wipes the buffer. Tests + a future "clear log"
   *  button consume this. */
  clear: () => void;
}

export function useMessageStream(): MessageStreamState {
  const [messages, setMessages] = useState<VmMessage[]>([]);
  const [hasReceivedAny, setHasReceivedAny] = useState(false);

  useEffect(() => {
    // SSR / test environments may not stub the bridge; bail rather
    // than throw. The hook stays observable (returns the initial
    // empty state) and the consumer's empty UX renders.
    if (typeof window === 'undefined' || !window.crucible) return;

    const unsubscribe = window.crucible.on(
      'vm:message-stream',
      (msg: VmMessage) => {
        setMessages((prev) => insertSorted(prev, msg));
        setHasReceivedAny(true);
      },
    );
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    messages,
    hasReceivedAny,
    clear: () => {
      setMessages([]);
      setHasReceivedAny(false);
    },
  };
}
